import React, { useEffect, useMemo, useRef, useState } from 'react';

// MUI (layout/table only)
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment
} from '@mui/material';

import DeleteOutlined from '@ant-design/icons/DeleteOutlined';

// your axios instance
import api from 'api/axios';

// ---------- helpers ----------
async function parseTxtCsv(file) {
  const text = await file.text();
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const hasHeader = lines.length && /phone/i.test(lines[0]);

  const rows = [];
  for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
    const [p, n] = lines[i].split(',').map((s) => s.trim());
    if (!p) continue;
    rows.push({ phone: p, name: n || '' });
  }

  const toE164ID = (v) => {
    let s = String(v).replace(/[^\d+]/g, '');
    if (s.startsWith('+')) s = s.slice(1);
    if (s.startsWith('62')) return '+' + s;
    if (s.startsWith('0')) return '+62' + s.slice(1);
    if (s.startsWith('8')) return '+62' + s;
    if (/^\d{10,15}$/.test(s)) return '+' + s;
    return null;
  };

  const result = [];
  const seen = new Set();
  rows.forEach((r) => {
    const e = toE164ID(r.phone);
    if (!e || seen.has(e)) return;
    seen.add(e);
    result.push({
      id: `${Date.now()}-${result.length}`,
      phone: e,
      name: r.name
    });
  });

  return result;
}

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);

  const pad = (n) => String(n).padStart(2, '0');
  const DD = pad(d.getDate());
  const MM = pad(d.getMonth() + 1);
  const YYYY = d.getFullYear();
  const HH = pad(d.getHours());
  const mm = pad(d.getMinutes());

  // format: 03/02/2026 09:41 (mengikuti timezone browser)
  return `${DD}/${MM}/${YYYY} ${HH}:${mm}`;
};

const normalizeUrl = (v) => {
  const s = String(v || '').trim();
  if (!s) return '';
  if (!/^https?:\/\//i.test(s)) return `https://${s}`;
  return s;
};

const stripHttp = (v) =>
  String(v || '')
    .trim()
    .replace(/^https?:\/\//i, '');

const isTerminalStatus = (status) => {
  const s = String(status || '').toLowerCase();
  return ['completed', 'failed', 'cancelled', 'canceled', 'done', 'finished'].includes(s);
};

// merge jobs (dedupe by sessionName|jobId)
const mergeJobsUnique = (prev, incoming) => {
  const a = Array.isArray(prev) ? prev : [];
  const b = Array.isArray(incoming) ? incoming : [];
  const map = new Map();
  [...a, ...b].forEach((j) => {
    const sessionName = j?.sessionName || '';
    const jobId = j?.jobId || j?.id || '';
    if (!sessionName || !jobId) return;
    const key = `${sessionName}|${jobId}`;

    // keep the one that has createdAt if possible
    const cur = map.get(key);
    if (!cur) {
      map.set(key, { ...j, jobId });
      return;
    }
    const curHas = !!cur?.createdAt;
    const nxtHas = !!j?.createdAt;
    if (!curHas && nxtHas) map.set(key, { ...cur, ...j, jobId });
  });

  // sort newest first by createdAt if available
  const arr = Array.from(map.values());
  arr.sort((x, y) => {
    const tx = x?.createdAt ? Date.parse(x.createdAt) : 0;
    const ty = y?.createdAt ? Date.parse(y.createdAt) : 0;
    return ty - tx;
  });
  return arr;
};

// ---------- "NO MUI" chip multi-select ----------
function SessionMultiSelect({ options, value, onChange, placeholder = 'Select sessions...' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = Array.isArray(value) ? value : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = Array.isArray(options) ? options : [];
    if (!q) return base;
    return base.filter((x) => String(x).toLowerCase().includes(q));
  }, [options, filter]);

  const add = (name) => {
    if (!name) return;
    if (selectedSet.has(name)) return;
    onChange([...selected, name]);
  };

  const remove = (name) => {
    onChange(selected.filter((x) => x !== name));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* field */}
      <div
        onClick={() => setOpen(true)}
        style={{
          minHeight: 42,
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 10,
          padding: 8,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          background: 'rgba(255,255,255,0.02)',
          cursor: 'text'
        }}
      >
        {selected.length === 0 ? (
          <span style={{ opacity: 0.6, fontSize: 14 }}>{placeholder}</span>
        ) : (
          selected.map((s) => (
            <span
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.10)',
                fontSize: 13
              }}
            >
              <span
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                }}
              >
                {s}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s);
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: 1
                }}
                aria-label={`remove-${s}`}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {/* dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            left: 0,
            right: 0,
            marginTop: 8,
            border: '1px solid rgba(255,255,255,0.16)',
            borderRadius: 12,
            background: '#111318',
            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: 10, borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter..."
              style={{
                width: '100%',
                height: 36,
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.16)',
                background: 'rgba(255,255,255,0.04)',
                color: 'white',
                padding: '0 12px',
                outline: 'none'
              }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12, opacity: 0.7, fontSize: 14 }}>No sessions</div>
            ) : (
              filtered.map((name) => {
                const isSel = selectedSet.has(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => add(name)}
                    disabled={isSel}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 0,
                      background: isSel ? 'rgba(255,255,255,0.08)' : 'transparent',
                      color: 'white',
                      cursor: isSel ? 'not-allowed' : 'pointer',
                      opacity: isSel ? 0.65 : 1
                    }}
                    title={isSel ? 'Already selected' : 'Select'}
                  >
                    <span
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                      }}
                    >
                      {name}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div
            style={{
              padding: 10,
              borderTop: '1px solid rgba(255,255,255,0.10)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end'
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setFilter('');
              }}
              style={{
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'transparent',
                color: 'white',
                borderRadius: 10,
                padding: '8px 10px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                border: 0,
                background: 'rgba(255,255,255,0.12)',
                color: 'white',
                borderRadius: 10,
                padding: '8px 10px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- component ----------
export default function Message() {
  const AUTH = useMemo(
    () => ({
      withCredentials: true,
      headers: { 'X-API-Key': 'secret123' }
    }),
    []
  );

  // sessions for selector
  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]); // array of sessionName

  // form fields (match API)
  const [intervalSeconds, setIntervalSeconds] = useState(2);
  const [maxMessagesPerSession, setMaxMessagesPerSession] = useState(1);

  // template builder inputs
  const [messageText, setMessageText] = useState('');

  // link buttons (UNLIMITED, optional) - default 1 row
  const [linkButtons, setLinkButtons] = useState([{ label: 'Link 1', url: '' }]);

  // files
  const [recipientsFile, setRecipientsFile] = useState(null);
  const [imageFile, setImageFile] = useState(null); // image or video
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  // recipients preview (optional)
  const [recipientsPreview, setRecipientsPreview] = useState([]);
  const [recPage, setRecPage] = useState(1);
  const [recPerPage, setRecPerPage] = useState(25);
  const recTotalPages = Math.max(1, Math.ceil(recipientsPreview.length / Math.max(1, recPerPage)));
  const pagedRecipients = useMemo(() => {
    const start = (recPage - 1) * recPerPage;
    return recipientsPreview.slice(start, start + recPerPage);
  }, [recipientsPreview, recPage, recPerPage]);
  useEffect(() => setRecPage(1), [recipientsPreview, recPerPage]);

  // sending & jobs
  const [isSending, setIsSending] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null); // {intervalSeconds,total...}

  // ✅ now jobs are historical (from /api/bulk-jobs/all + new jobs appended)
  // store as: [{jobId, sessionName, createdAt?, id?}]
  const [jobs, setJobs] = useState([]);

  // key: `${sessionName}|${jobId}` => status payload
  const [jobStates, setJobStates] = useState({});

  // for media preview
  const [imagePreview, setImagePreview] = useState('');

  // bulk jobs list fetch state
  const [jobsAllLoading, setJobsAllLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const pollersRef = useRef({}); // key => intervalId

  const fetchActiveSessions = async () => {
    try {
      const res = await api.get('/api/sessions/active', AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.sessions) ? payload.sessions : [];

      const uniq = [];
      const seen = new Set();
      list.forEach((s) => {
        const name = s?.sessionName;
        if (!name || seen.has(name)) return;
        seen.add(name);
        uniq.push(name);
      });

      setSessionOptions(uniq);
    } catch (e) {
      console.error('fetch sessions list error:', e);
      setSessionOptions([]);
    }
  };

  // ✅ NEW: fetch all historical bulk jobs
  const fetchAllBulkJobs = async () => {
    setJobsAllLoading(true);
    try {
      const res = await api.get('/api/bulk-jobs/all', AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.jobs) ? payload.jobs : [];

      // normalize to {jobId, sessionName, createdAt, id}
      const normalized = list
        .map((j) => ({
          id: j?.id,
          jobId: j?.jobId,
          sessionName: j?.sessionName,
          createdAt: j?.createdAt
        }))
        .filter((j) => j.jobId && j.sessionName);

      setJobs((prev) => mergeJobsUnique(prev, normalized));

      // start polling for anything not terminal (or not yet loaded)
      normalized.forEach((j) => {
        const key = `${j.sessionName}|${j.jobId}`;
        const existing = jobStates[key];
        if (existing && isTerminalStatus(existing?.status)) return;
        pollJob(j.sessionName, j.jobId);
      });
    } catch (e) {
      console.error('fetch bulk jobs all error:', e);
    } finally {
      setJobsAllLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveSessions();
    fetchAllBulkJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAll = () => {
    setRecipientsFile(null);
    setImageFile(null);
    setRecipientsPreview([]);
    setRecPage(1);
    setBulkSummary(null);
    // ✅ do NOT clear jobs/jobStates (history must remain)
  };

  const onRecipientsChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setRecipientsFile(f);

    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (ext === 'txt' || ext === 'csv') {
      try {
        const rows = await parseTxtCsv(f);
        setRecipientsPreview(rows);
      } catch {
        setRecipientsPreview([]);
      }
    } else {
      setRecipientsPreview([]);
    }

    e.target.value = '';
  };

  const onImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setImageFile(f);

    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(f));

    e.target.value = '';
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview('');
    setImageFile(null);
  };

  const stopAllPollers = () => {
    const map = pollersRef.current || {};
    Object.keys(map).forEach((k) => {
      clearInterval(map[k]);
      delete map[k];
    });
  };

  useEffect(() => {
    return () => stopAllPollers();
  }, []);

  const pollJob = (sessionName, jobId) => {
    const key = `${sessionName}|${jobId}`;

    // prevent duplicate pollers
    if (pollersRef.current[key]) return;

    const tick = async () => {
      try {
        const res = await api.get(`/api/sessions/${encodeURIComponent(sessionName)}/messages/${encodeURIComponent(jobId)}`, AUTH);
        const data = res?.data || null;

        setJobStates((prev) => ({ ...prev, [key]: data }));

        const status = String(data?.status || '').toLowerCase();
        if (status && isTerminalStatus(status)) {
          if (pollersRef.current[key]) {
            clearInterval(pollersRef.current[key]);
            delete pollersRef.current[key];
          }
        }
      } catch (e) {
        setJobStates((prev) => ({
          ...prev,
          [key]: prev[key] || {
            id: jobId,
            sessionName,
            status: 'unknown',
            errors: [{ error: e?.message || 'poll error' }]
          }
        }));
      }
    };

    tick();
    pollersRef.current[key] = setInterval(tick, 1500);
  };

  // ------- link buttons helpers (UNLIMITED) -------
  const addLinkRow = () => {
    setLinkButtons((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const n = list.length + 1;
      list.push({ label: `Link ${n}`, url: '' });
      return list;
    });
  };

  const removeLinkRow = (idx) => {
    setLinkButtons((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      list.splice(idx, 1);
      return list.map((x, i) => ({ ...x, label: `Link ${i + 1}` }));
    });
  };

  const updateLinkRow = (idx, key, value) => {
    setLinkButtons((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const row = list[idx] || { label: `Link ${idx + 1}`, url: '' };
      list[idx] = { ...row, [key]: value };
      return list;
    });
  };

  const buildTemplateFromInputs = () => {
    const body = String(messageText || '').trim();

    const tpl = {
      name: 'promo_button',
      components: [
        {
          type: 'body',
          parameters: [{ type: 'text', text: body }]
        }
      ]
    };

    const filled = (Array.isArray(linkButtons) ? linkButtons : [])
      .map((b, i) => ({
        label: String(b?.label || `Link ${i + 1}`).trim() || `Link ${i + 1}`,
        url: normalizeUrl(b?.url)
      }))
      .filter((x) => !!x.url);

    filled.forEach((x, idx) => {
      tpl.components.push({
        type: 'button',
        sub_type: 'url',
        index: String(idx),
        text: x.label || `Link ${idx + 1}`,
        parameters: [{ type: 'text', text: x.url }]
      });
    });

    return { tpl, body, links: filled };
  };

  const startBulkSend = async () => {
    if (!selectedSessions.length) return alert('Pilih minimal 1 sessionName');
    if (!recipientsFile) return alert('File recipients belum dipilih');

    const { tpl, body, links } = buildTemplateFromInputs();
    if (!body) return alert('Message wajib diisi');

    for (const b of links) {
      if (b.url && !/^https?:\/\/\S+$/i.test(b.url)) {
        return alert(`Format link tidak valid: ${b.url}`);
      }
    }

    setIsSending(true);
    setBulkSummary(null);

    // ✅ IMPORTANT:
    // do NOT clear jobs/jobStates and do NOT stop pollers,
    // so history stays visible even when user starts new cycle.

    try {
      const fd = new FormData();
      fd.append('sessionNames', selectedSessions.join(','));
      fd.append('intervalSeconds', String(Math.max(0, Number(intervalSeconds || 0))));
      fd.append('maxMessagesPerSession', String(Math.max(1, Number(maxMessagesPerSession || 1))));
      fd.append('recipients', recipientsFile);
      if (imageFile) fd.append('image', imageFile);
      fd.append('template', JSON.stringify(tpl));

      const res = await api.post('/api/messages/bulk', fd, AUTH);
      const payload = res?.data || {};

      setBulkSummary({
        intervalSeconds: payload.intervalSeconds,
        totalMessages: payload.totalMessages,
        totalRecipients: payload.totalRecipients,
        totalSessions: payload.totalSessions
      });

      const jobList = Array.isArray(payload.jobs) ? payload.jobs : [];
      const normalizedNew = jobList
        .map((j) => ({
          jobId: j?.jobId,
          sessionName: j?.sessionName
        }))
        .filter((j) => j.jobId && j.sessionName);

      // ✅ append to history list (dedupe)
      setJobs((prev) => mergeJobsUnique(prev, normalizedNew));

      // poll new jobs
      normalizedNew.forEach((j) => pollJob(j.sessionName, j.jobId));

      // ✅ refresh /api/bulk-jobs/all so createdAt also appears + keeps backend as source of truth
      fetchAllBulkJobs();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || err?.message || 'Gagal bulk send');
    } finally {
      setIsSending(false);
    }
  };

  const removeJobCard = (sessionName, jobId) => {
    const key = `${sessionName}|${jobId}`;
    if (pollersRef.current[key]) {
      clearInterval(pollersRef.current[key]);
      delete pollersRef.current[key];
    }
    setJobs((prev) => prev.filter((j) => !(j.sessionName === sessionName && j.jobId === jobId)));
    setJobStates((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {/* BULK MESSAGE */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Bulk Message Sender" />
            <CardContent>
              <Grid container spacing={2}>
                {/* LEFT */}
                <Grid item xs={12} md={5}>
                  <Stack spacing={2}>
                    {/* Sessions selector (NO MUI) */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        sessionNames (multi)
                      </Typography>

                      <SessionMultiSelect
                        options={sessionOptions}
                        value={selectedSessions}
                        onChange={setSelectedSessions}
                        placeholder="Choose active sessions..."
                      />

                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedSessions(sessionOptions)}
                          disabled={sessionOptions.length === 0}
                        >
                          Select All
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => setSelectedSessions([])}
                          disabled={selectedSessions.length === 0}
                        >
                          Clear
                        </Button>

                        <Button size="small" variant="outlined" onClick={fetchActiveSessions}>
                          Refresh sessions
                        </Button>

                        <Box sx={{ flex: 1 }} />

                        <Typography variant="caption" sx={{ opacity: 0.75, alignSelf: 'center' }}>
                          Selected: <b>{selectedSessions.length}</b> / {sessionOptions.length}
                        </Typography>
                      </Stack>

                      <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.75 }}>
                        Akan dikirim sebagai: <b>test-1,test-2</b> (comma separated)
                      </Typography>
                    </Box>

                    {/* interval + max per session */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="intervalSeconds"
                          type="number"
                          fullWidth
                          value={intervalSeconds}
                          onChange={(e) => setIntervalSeconds(Math.max(0, Number(e.target.value || 0)))}
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="maxMessagesPerSession"
                          type="number"
                          fullWidth
                          value={maxMessagesPerSession}
                          onChange={(e) => setMaxMessagesPerSession(Math.max(1, Number(e.target.value || 1)))}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                    </Grid>

                    {/* Inputs template builder */}
                    <TextField
                      label="Message (template body) *"
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={12}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder={`Contoh:\nAyo Segera Main`}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                          e.preventDefault();
                          startBulkSend();
                        }
                      }}
                    />

                    {/* Link buttons builder (UNLIMITED) */}
                    <Box
                      sx={{
                        p: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Buttons URL (optional, unlimited)</Typography>
                        <Button size="small" variant="outlined" onClick={addLinkRow}>
                          Add button
                        </Button>
                      </Stack>

                      {linkButtons.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.75 }}>
                          Tidak ada button (opsional). Klik &quot;Add button&quot; untuk menambah.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {linkButtons.map((b, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center'
                              }}
                            >
                              <TextField
                                label={`Text #${idx + 1}`}
                                value={b.label || `Link ${idx + 1}`}
                                onChange={(e) => updateLinkRow(idx, 'label', e.target.value)}
                                sx={{ flex: 1 }}
                                placeholder={`Link ${idx + 1}`}
                              />

                              <TextField
                                label={`URL #${idx + 1}`}
                                value={stripHttp(b.url)}
                                onChange={(e) => {
                                  const stripped = stripHttp(e.target.value);
                                  updateLinkRow(idx, 'url', stripped);
                                }}
                                sx={{ flex: 2 }}
                                placeholder="abc.com"
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">https://</InputAdornment>
                                }}
                              />

                              <Tooltip title="Remove">
                                <IconButton size="small" color="error" onClick={() => removeLinkRow(idx)}>
                                  <DeleteOutlined />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            Tip: ketik domain saja (abc.com). Kalau paste https://abc.com tetap aman.
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    {/* Upload recipients */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        recipients (TXT/CSV file)
                      </Typography>
                      <input ref={fileRef} type="file" hidden accept=".txt,.csv" onChange={onRecipientsChange} />
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" onClick={() => fileRef.current?.click()}>
                          Choose recipients
                        </Button>
                        <Button variant="outlined" color="error" onClick={clearAll}>
                          Clear
                        </Button>
                      </Stack>

                      {recipientsFile && (
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                          File: <b>{recipientsFile.name}</b>
                        </Typography>
                      )}
                    </Box>

                    {/* Upload media (image / video) */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        media (image / video, optional)
                      </Typography>

                      <input ref={imgRef} type="file" hidden accept="image/*,video/*" onChange={onImageChange} />

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" onClick={() => imgRef.current?.click()}>
                          Choose media
                        </Button>
                        <Button variant="outlined" color="error" onClick={removeImage} disabled={!imageFile}>
                          Remove
                        </Button>
                      </Stack>

                      {imageFile && (
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                          Media: <b>{imageFile.name}</b>{' '}
                          <span style={{ fontSize: 12, opacity: 0.7 }}>({imageFile.type || 'unknown type'})</span>
                        </Typography>
                      )}

                      {imagePreview && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.75 }}>
                            Preview
                          </Typography>

                          {imageFile?.type?.startsWith('video') ? (
                            <Box
                              component="video"
                              src={imagePreview}
                              controls
                              sx={{
                                width: '100%',
                                maxHeight: 260,
                                objectFit: 'contain',
                                borderRadius: 1
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={imagePreview}
                              alt="Preview"
                              sx={{
                                width: '100%',
                                maxHeight: 260,
                                objectFit: 'contain',
                                borderRadius: 1
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Actions */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={startBulkSend}
                        disabled={isSending || !recipientsFile || selectedSessions.length === 0}
                      >
                        {isSending ? 'Sending...' : 'Send Bulk'}
                      </Button>
                      <Button variant="outlined" onClick={() => stopAllPollers()}>
                        Stop Polling
                      </Button>
                    </Stack>

                    {bulkSummary && (
                      <Box
                        sx={{
                          p: 1.25,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Summary:
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          intervalSeconds: <b>{bulkSummary.intervalSeconds}</b>
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          totalSessions: <b>{bulkSummary.totalSessions}</b> • totalRecipients: <b>{bulkSummary.totalRecipients}</b> •
                          totalMessages: <b>{bulkSummary.totalMessages}</b>
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* RIGHT */}
                <Grid item xs={12} md={7}>
                  {/* Jobs status */}
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="h6">Jobs Process</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button size="small" variant="outlined" onClick={fetchAllBulkJobs} disabled={jobsAllLoading}>
                          {jobsAllLoading ? 'Loading...' : 'Reload Jobs'}
                        </Button>
                      </Stack>
                    </Stack>

                    {jobs.length === 0 ? (
                      <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Belum ada job. Setelah Send Bulk, jobId akan muncul. History juga diambil dari /api/bulk-jobs/all.
                      </Typography>
                    ) : (
                      <Stack spacing={1.25}>
                        {jobs.map((j) => {
                          const key = `${j.sessionName}|${j.jobId}`;
                          const st = jobStates[key];
                          const status = String(st?.status || 'pending').toLowerCase();
                          const startedAt = st?.startedAt || '';
                          const finishedAt = st?.finishedAt || '';

                          return (
                            <Box
                              key={key}
                              sx={{
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                display: 'flex',
                                gap: 2,
                                alignItems: 'flex-start'
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">
                                  sessionName: <b>{j.sessionName}</b> • jobId:{' '}
                                  <span
                                    style={{
                                      fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                                    }}
                                  >
                                    {j.jobId}
                                  </span>
                                </Typography>

                                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                                  status: <b>{status}</b>
                                  {typeof st?.sent === 'number' ? (
                                    <>
                                      {' '}
                                      • sent: <b>{st.sent}</b> • failed: <b>{st.failed}</b> • total: <b>{st.total}</b>
                                    </>
                                  ) : null}
                                </Typography>

                                {Array.isArray(st?.errors) && st.errors.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Errors:
                                    </Typography>
                                    <Box
                                      component="ul"
                                      sx={{
                                        m: 0,
                                        pl: 2.2,
                                        mt: 0.5,
                                        '& li': { fontSize: 13, opacity: 0.85 }
                                      }}
                                    >
                                      {st.errors.slice(0, 6).map((e, idx) => (
                                        <li key={idx}>
                                          to: <b>{e?.to}</b> • {e?.error}
                                        </li>
                                      ))}
                                      {st.errors.length > 6 ? <li>…and {st.errors.length - 6} more</li> : null}
                                    </Box>
                                  </Box>
                                )}

                                {startedAt || finishedAt ? (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
                                    started: {formatDateTime(startedAt)} • finished: {formatDateTime(finishedAt)}
                                  </Typography>
                                ) : null}
                              </Box>

                              <Stack spacing={1} alignItems="flex-end">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => pollJob(j.sessionName, j.jobId)}
                                  disabled={!!pollersRef.current[key] && !isTerminalStatus(status)}
                                >
                                  Refresh
                                </Button>

                                <Tooltip title="Remove card">
                                  <IconButton size="small" color="error" onClick={() => removeJobCard(j.sessionName, j.jobId)}>
                                    <DeleteOutlined />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Recipients preview table (optional) */}
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Recipients Preview ({recipientsPreview.length})
                  </Typography>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box component="tr">
                          {['#', 'Phone', 'Name'].map((h) => (
                            <Box
                              key={h}
                              component="th"
                              sx={{
                                textAlign: 'left',
                                fontWeight: 600,
                                p: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              {h}
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      <Box component="tbody">
                        {recipientsPreview.length === 0 ? (
                          <Box component="tr">
                            <Box component="td" colSpan={3} sx={{ p: 1, opacity: 0.8 }}>
                              Upload TXT/CSV untuk preview
                            </Box>
                          </Box>
                        ) : (
                          pagedRecipients.map((r, i) => {
                            const startIndex = (recPage - 1) * recPerPage;
                            return (
                              <Box component="tr" key={r.id}>
                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  {startIndex + i + 1}
                                </Box>

                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {r.phone}
                                  </Typography>
                                </Box>

                                <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  {r.name || '-'}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total: {recipientsPreview.length} | Page {recPage} / {recTotalPages}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Rows:
                      </Typography>
                      <Select size="small" value={recPerPage} onChange={(e) => setRecPerPage(Number(e.target.value))}>
                        {[25, 50, 100, 200].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>

                      <Button size="small" onClick={() => setRecPage((p) => Math.max(1, p - 1))} disabled={recPage <= 1}>
                        Prev
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setRecPage((p) => Math.min(recTotalPages, p + 1))}
                        disabled={recPage >= recTotalPages}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
